import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { summariseBriefAsk } from '@/app/common/brief-ask/summarise-brief-ask';
import { DeliveryTypeEnum } from '../../../../common/general-types/briefs.dto';
import { ImageService } from '../../../../services/image/image.service';

interface SummaryLine {
  key: string;
  value: string;
}

/** Live brief summary card (dark surface) shown in the review rails of both flows. */
@Component({
  selector: 'bb-summary-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage],
  template: `
    <div class="rounded-xl bg-brand-deep-purple p-6 text-white">
      <div>
        <div class="text-[11px] font-bold uppercase tracking-wider text-brand-studio">{{ eyebrow() }}</div>
        <div class="mt-1.5 font-heading text-2xl font-bold leading-none">{{ title() }}</div>
      </div>

      @if (product(); as p) {
        <div class="mt-4 flex items-center gap-2.5 rounded-md bg-white/[0.06] p-3">
          @if (p.productImage) {
            <img [ngSrc]="imageService.getImageSource(p.productImage, 'gallery')" width="40" height="40" class="size-10 rounded object-cover" alt="" />
          }
          <div class="min-w-0">
            <div class="truncate font-semibold">{{ p.productName }}</div>
            <div class="truncate text-[11px] text-white/60">{{ p.productCategory }}</div>
          </div>
        </div>
      }

      <div class="mt-4 flex flex-col">
        @for (line of lines(); track line.key) {
          <div class="flex justify-between gap-3 border-b border-white/10 py-2.5 text-[13px]">
            <span class="text-white/55">{{ line.key }}</span>
            <span class="text-right font-semibold">{{ line.value }}</span>
          </div>
        }
      </div>
    </div>
  `,
})
export class BbSummaryCard {
  private readonly service = inject(CreateBriefService);
  public imageService = inject(ImageService);

  readonly eyebrow = computed(() =>
    this.service.createShoutBrief() ? 'Posted social brief' : 'UGC-only brief',
  );
  readonly title = computed(() => this.service.basicInfoModel().briefTitle || 'Untitled brief');
  readonly product = computed(() => this.service.selectedProduct());

  readonly lines = computed<SummaryLine[]>(() => {
    const s = this.service;
    const out: SummaryLine[] = [];
    const goal = s.selectedBriefGoal();
    if (goal) {
      out.push({ key: 'Goal', value: goal.name });
    }
    out.push({ key: 'Creators', value: String(s.requiredCreators()) });
    const primary = s.selectedPrimaryChannel();
    const ask = summariseBriefAsk({
      pieces: s.pieces(),
      videosPerCreator: s.videosPerCreator(),
      shoutBrief: primary ? { socialPlatform: primary.channelId } : null,
    });
    if (ask.source === 'pieces') {
      out.push({ key: 'Pieces', value: ask.label });
      out.push({ key: 'Posts to', value: ask.detail });
    }
    const format = s.selectedVideoFormat();
    if (format) {
      out.push({ key: 'Format', value: `${format.description} (${format.name})` });
    }
    const duration = s.selectedVideoDuration();
    if (duration) {
      out.push({ key: 'Duration', value: duration.name });
    }
    const delivery = s.deliveryType();
    out.push({ key: 'Delivery', value: this.deliveryLabel(delivery) });
    if (ask.source !== 'pieces' && s.createShoutBrief() && primary) {
      out.push({ key: 'Posts to', value: primary.shortName });
      const cross = s.selectedCrossPlatformChannels();
      if (cross.length) {
        out.push({ key: 'Cross-post', value: `${cross.length} more` });
      }
    }
    const ages = s.selectedAge();
    if (ages.length) {
      out.push({ key: 'Age', value: ages.map((a) => a.label).join(', ') });
    }
    const concepts = s.creativeConcepts();
    if (concepts.length) {
      out.push({ key: 'Concepts', value: String(concepts.length) });
    }
    const screening = s.screeningQuestions();
    if (screening.length) {
      out.push({ key: 'Screening', value: `${screening.length} questions` });
    }
    return out;
  });

  private deliveryLabel(type: DeliveryTypeEnum): string {
    switch (type) {
      case DeliveryTypeEnum.Discount_Code:
        return 'Discount code';
      case DeliveryTypeEnum.Address_Required:
        return 'Ship a product';
      default:
        return 'No delivery';
    }
  }
}
